import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import { paths } from '@/utils/path'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useNavigate } from 'react-router-dom'
import { Button, FormItem } from 'vnetwork-ui'
import { createPromo } from '../../services'
import { CreatePromoRequest } from '../../types'
import { createPromoSchema } from './schema'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { PROMO_TYPE_OPTIONS } from '../../constants'
import { Checkbox, FormControlLabel } from '@mui/material'
import { Controller } from 'react-hook-form'

export interface PromoCreateFormValues {
    code: string
    name: string
    description?: string
    promo_type: string
    discount_value: number
    max_discount?: number
    currency: string
    max_uses?: number
    max_uses_per_customer: number
    valid_from?: string
    valid_until?: string
    min_order_amount?: number
    new_customers_only: boolean
}

const New = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const { control, handleSubmit, watch } = useForm<PromoCreateFormValues>({
        defaultValues: {
            code: '',
            name: '',
            description: '',
            promo_type: 'percentage',
            discount_value: 10,
            max_discount: undefined,
            currency: 'VND',
            max_uses: undefined,
            max_uses_per_customer: 1,
            valid_from: '',
            valid_until: '',
            min_order_amount: undefined,
            new_customers_only: false,
        },
        resolver: yupResolver(createPromoSchema) as any,
        reValidateMode: 'onChange',
    })

    const promoType = watch('promo_type')

    const { mutateAsync: submit, isPending } = useMutationWithHandler(
        {
            mutationFn: (data: CreatePromoRequest) => createPromo(data),
        },
        {
            successMessage: t('promo_created_successfully'),
            reactQueryKeys: QUERY_KEYS.PROMOS,
            onSuccess: () => {
                navigate(paths.promosList)
            },
        }
    )

    const onSubmit = async (data: PromoCreateFormValues) => {
        await submit({
            code: data.code.toUpperCase(),
            name: data.name,
            description: data.description || undefined,
            promo_type: data.promo_type as any,
            discount_value: data.discount_value,
            max_discount: data.max_discount || undefined,
            currency: data.currency || 'VND',
            max_uses: data.max_uses || undefined,
            max_uses_per_customer: data.max_uses_per_customer,
            valid_from: data.valid_from || undefined,
            valid_until: data.valid_until || undefined,
            min_order_amount: data.min_order_amount || undefined,
            new_customers_only: data.new_customers_only,
        })
    }

    return (
        <div>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.promosList,
                        title: t('promo_manager'),
                    },
                    {
                        path: paths.promoCreate,
                        title: t('create_promo'),
                    },
                ]}
            />

            <Paper className="mt-4">
                <h2 className="text-lg font-semibold mb-4">
                    {t('create_promo')}
                </h2>
                <form onSubmit={handleSubmit(onSubmit)} data-tour="promo-form">
                    <h3
                        className="text-sm font-medium text-gray-500 mb-3"
                        data-tour="promo-basic-section"
                    >
                        {t('basic_information')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div data-tour="promo-code">
                            <FormItem
                                control={control}
                                name="code"
                                label={t('code')}
                                type="textfield"
                                placeholder="e.g., NEWYEAR2025"
                            />
                        </div>

                        <div data-tour="promo-name">
                            <FormItem
                                control={control}
                                name="name"
                                label={t('name')}
                                type="textfield"
                            />
                        </div>

                        <div
                            className="md:col-span-2"
                            data-tour="promo-description"
                        >
                            <FormItem
                                control={control}
                                name="description"
                                label={t('description')}
                                type="textfield"
                            />
                        </div>
                    </div>

                    <h3
                        className="text-sm font-medium text-gray-500 mb-3 mt-6"
                        data-tour="promo-discount-section"
                    >
                        {t('discount_settings')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div data-tour="promo-type">
                            <FormItem
                                control={control}
                                name="promo_type"
                                label={t('type')}
                                type="select"
                                options={PROMO_TYPE_OPTIONS(t)}
                            />
                        </div>

                        <div data-tour="promo-value">
                            <FormItem
                                control={control}
                                name="discount_value"
                                label={
                                    promoType === 'percentage'
                                        ? t('discount_percent')
                                        : t('discount_amount')
                                }
                                type="number"
                            />
                        </div>

                        {promoType === 'percentage' && (
                            <div data-tour="promo-max-discount">
                                <FormItem
                                    control={control}
                                    name="max_discount"
                                    label={t('max_discount')}
                                    type="number"
                                    placeholder={t('optional')}
                                />
                            </div>
                        )}

                        <div data-tour="promo-min-order">
                            <FormItem
                                control={control}
                                name="min_order_amount"
                                label={t('min_order_amount')}
                                type="number"
                                placeholder={t('optional')}
                            />
                        </div>
                    </div>

                    <h3
                        className="text-sm font-medium text-gray-500 mb-3 mt-6"
                        data-tour="promo-usage-section"
                    >
                        {t('usage_limits')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div data-tour="promo-max-uses">
                            <FormItem
                                control={control}
                                name="max_uses"
                                label={t('max_uses')}
                                type="number"
                                placeholder={t('unlimited')}
                            />
                        </div>

                        <div data-tour="promo-max-per-customer">
                            <FormItem
                                control={control}
                                name="max_uses_per_customer"
                                label={t('max_uses_per_customer')}
                                type="number"
                            />
                        </div>

                        <div data-tour="promo-valid-from">
                            <FormItem
                                control={control}
                                name="valid_from"
                                label={t('valid_from')}
                                type="datepicker"
                            />
                        </div>

                        <div data-tour="promo-valid-until">
                            <FormItem
                                control={control}
                                name="valid_until"
                                label={t('valid_until')}
                                type="datepicker"
                            />
                        </div>

                        <div
                            className="md:col-span-2"
                            data-tour="promo-new-customers"
                        >
                            <Controller
                                name="new_customers_only"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={field.value}
                                                onChange={field.onChange}
                                            />
                                        }
                                        label={t('new_customers_only')}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <div
                        className="flex gap-2 justify-end mt-6"
                        data-tour="promo-submit"
                    >
                        <Button
                            variant="outlined"
                            onClick={() => navigate(paths.promosList)}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            loading={isPending}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </form>
            </Paper>
        </div>
    )
}

export default New
